import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { cargoSideFor } from './cargoSequence';
import { CargoPackageVisual, CargoPanda } from './CargoVisuals';
import CargoGroupRuleCard from './CargoGroupRuleCard';

const LABELS = {
  blue: 'MAVİ', yellow: 'SARI', purple: 'MOR', orange: 'TURUNCU',
  box: 'KUTU', envelope: 'ZARF', tube: 'TÜP', bag: 'POŞET',
};
const COLOR_HEX = { blue: '#2563EB', yellow: '#FACC15', purple: '#9360F1', orange: '#FFAE2B' };

function MiniRule({ rule, caption }) {
  const colorRule = rule?.rule_type === 'color';
  const fallback = colorRule ? ['blue', 'yellow'] : ['box', 'envelope'];
  const value = (Array.isArray(rule?.values) ? rule.values : fallback)[0];
  const side = cargoSideFor(rule, colorRule ? { color: value } : { package_type: value });
  return (
    <Box sx={{ bgcolor: '#FAF8FF', border: '2px solid #E8DFFA', borderRadius: 3.5, p: { xs: 1, sm: 1.6 }, minWidth: 0, flex: 1 }}>
      <Typography sx={{ fontSize: { xs: 11, sm: 13 }, fontWeight: 1000, color: '#7541B8' }}>{caption}</Typography>
      <Typography sx={{ fontSize: { xs: 10, sm: 12 }, fontWeight: 900, color: '#6D5996', mt: .2 }}>
        {colorRule ? 'RENGİNE BAK' : 'TÜRÜNE BAK'}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .7, mt: 1 }}>
        {colorRule
          ? <Box sx={{ height: { xs: 36, sm: 49 }, width: { xs: 36, sm: 49 }, bgcolor: COLOR_HEX[value] || '#9360F1', border: '3px solid #6941AE', borderRadius: '50%', boxShadow: 'inset 0 -4px 0 rgba(0,0,0,.12)' }} />
          : <CargoPackageVisual color="blue" packageType={value} size={49} />}
        <Typography sx={{ fontSize: { xs: 16, sm: 20 }, fontWeight: 1000, color: '#7541B8' }}>→</Typography>
        <Box sx={{ bgcolor: '#ECE8FA', border: '2px solid #D5C4F9', borderRadius: 99, px: { xs: .8, sm: 1.3 }, py: .5 }}>
          <Typography sx={{ color: '#542A98', fontSize: { xs: 11, sm: 13 }, fontWeight: 1000 }}>{side === 'left' ? 'SOL' : 'SAĞ'}</Typography>
        </Box>
      </Box>
      <Typography sx={{ color: '#4C258B', fontWeight: 1000, fontSize: { xs: 11, sm: 13 }, mt: .4 }}>
        {LABELS[value] || value}
      </Typography>
    </Box>
  );
}

export default function CargoRuleChangeNotice({ beforeRule, afterRule, onContinue, manual = false }) {
  const changeType = afterRule?.change_type;
  const featureChanged = changeType
    ? ['feature', 'both'].includes(changeType)
    : beforeRule?.rule_type !== afterRule?.rule_type;
  const directionChanged = changeType
    ? ['direction', 'both'].includes(changeType)
    : beforeRule?.mapping !== afterRule?.mapping;
  const description = featureChanged && directionChanged ? 'Hem özellik hem yön değişti!'
    : featureChanged ? 'Artık farklı bir özelliğe bakıyoruz!'
    : directionChanged ? 'Yönler değişti! Yeni tabelalara dikkat et.'
    : 'Yeni kurala dikkat et.';
  return (
    <Box sx={{ textAlign: 'center', mx: 'auto', width: '100%', maxWidth: 520, p: { xs: 1.5, sm: 3 }, bgcolor: '#fff', border: '3px solid #E6D8FF', borderRadius: { xs: 4, sm: 6 }, boxShadow: '0 8px 0 #D4C3F6, 0 20px 45px rgba(50,24,99,.12)' }}>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}><CargoPanda size={72} /></Box>
      <Typography sx={{ color: '#7B4ADE', fontWeight: 1000, fontSize: 12, letterSpacing: '.1em' }}>⚡ DİKKAT, YENİ KURAL!</Typography>
      <Typography sx={{ mt: .6, color: '#51289B', fontWeight: 1000, fontSize: { xs: 29, sm: 39 }, lineHeight: 1.05 }}>KURAL DEĞİŞTİ!</Typography>
      <Typography sx={{ mt: .6, color: '#69528D', fontWeight: 850, fontSize: { xs: 12, sm: 16 } }}>{description}</Typography>
      <Box sx={{
        mt: { xs: 1.4, sm: 2.5 }, display: 'flex',
        flexDirection: beforeRule?.groups || afterRule?.groups
          ? { xs: 'column', sm: 'row' } : 'row',
        alignItems: 'stretch', gap: { xs: .8, sm: 1.2 },
        maxHeight: { xs: '63dvh', sm: 'none' }, overflowY: { xs: 'auto', sm: 'visible' },
      }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ color: '#7541B8', fontWeight: 1000, fontSize: 12 }}>ÖNCE</Typography>
          {beforeRule?.groups ? <CargoGroupRuleCard rule={beforeRule} compact /> : <MiniRule rule={beforeRule} caption="" />}
        </Box>
        <Box sx={{ display: 'grid', placeItems: 'center', color: '#8354DC',
          fontWeight: 1000, fontSize: { xs: 17, sm: 25 },
          transform: beforeRule?.groups || afterRule?.groups ? { xs: 'rotate(90deg)', sm: 'none' } : 'none',
        }}>➜</Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ color: '#7541B8', fontWeight: 1000, fontSize: 12 }}>ŞİMDİ</Typography>
          {afterRule?.groups ? <CargoGroupRuleCard rule={afterRule} compact /> : <MiniRule rule={afterRule} caption="" />}
        </Box>
      </Box>
      {manual ? (
        <Button
          data-testid="cargo-rule-continue"
          onClick={onContinue}
          variant="contained"
          sx={{ mt: 2.2, minHeight: 50, borderRadius: 99, px: 3, bgcolor: '#7650DC', color: '#fff', border: '3px solid #5D33BA', boxShadow: '0 5px 0 #492698', fontSize: { xs: 15, sm: 18 }, fontWeight: 1000, textTransform: 'none', '&:hover': { bgcolor: '#6940D3' }, '&:focus-visible': { outline: '4px solid #FFB728', outlineOffset: 3 } }}
        >
          Yeni kuralla devam! →
        </Button>
      ) : null}
    </Box>
  );
}
